'use client';

import Image from 'next/image';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { AmplitudeContext } from '@/contexts/amplitude';
import { FeatureImageCardsProps, ManageBusinessProps } from '@/types/cms';

import { MarkdownContent } from '../v4/MarkdownContent';
import styles from './ListWithImages.module.css';

export interface ListWithImagesProps
  extends Omit<ManageBusinessProps, 'buttons'> {
  children: ReactNode;
  fullImage?: boolean;
  isTextAtTop?: boolean;
  objectFit?: string;
}

const ListWithImages: React.FC<ListWithImagesProps> = ({
  referId,
  title,
  tag,
  cards,
  children,
  fullImage,
  isTextAtTop,
  objectFit = 'object-cover',
}) => {
  const [currentListCard, setCurrentListCard] =
    useState<FeatureImageCardsProps>({} as FeatureImageCardsProps);

  const listCardItemAction = (
    e: React.MouseEvent,
    card: FeatureImageCardsProps,
  ): void => {
    const allListWrapperEl = document.querySelectorAll(
      '[data-card-name="listWrapper"]',
    );

    const targetElement = e.target as HTMLElement;
    const listCardItem = targetElement.closest(
      '[data-card-name="listWrapper"]',
    );

    allListWrapperEl.forEach(list => list.classList.remove('listItemActive'));

    listCardItem && listCardItem.classList.add('listItemActive');

    setCurrentListCard(card);
  };

  const { analytics } = useContext(AmplitudeContext);

  const [ref, inView] = useInView({
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      analytics?.track({
        event_type: 'section viewed',
        event_properties: {
          name: 'section viewed',
          description:
            'Evento disparado quando o usuário visualiza uma sessão especifica da pagina',
          section_reference: referId,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analytics, inView]);

  return (
    <div
      ref={ref}
      id={referId}
      className="flex flex-col justify-center md:justify-start items-center py-40 lg:py-80 md:px-24 mx-auto max-w-[455px] md:max-w-[1108px]"
    >
      <div
        className={`${
          isTextAtTop ? 'hidden' : 'flex'
        } flex flex-col md:justify-center px-16 lg:px-0 w-full md:text-center lg:text-left`}
      >
        <p className="mb-8 md:mb-16 w-full max-w-[455px] md:max-w-full font-bold text-stone-700 uppercase paragraph-14">
          {tag}
        </p>

        <h2 className="mb-16 w-full max-w-[510px] font-display text-[32px] md:text-3xl lg:text-[32px] leading-[40px] md:leading-[36px] lg:leading-[40px] heading-3">
          {title}
        </h2>
      </div>
      <div className="flex relative lg:gap-40 justify-center lg:justify-start items-start mx-auto md:mx-24 lg:mx-0 md:w-full max-w-[455px] md:max-w-full">
        <div className="w-full md:max-w-[510px]">
          <div
            className={`${
              !isTextAtTop ? 'hidden' : 'flex'
            } flex flex-col md:justify-center px-16 lg:px-0 w-full md:text-center lg:text-left`}
          >
            <p className="mb-8 md:mb-16 w-full max-w-[455px] md:max-w-full font-bold text-stone-700 uppercase paragraph-14">
              {tag}
            </p>

            <h2 className="mb-16 w-full max-w-[510px] font-display text-[32px] md:text-3xl lg:text-[32px] leading-[40px] md:leading-[36px] lg:leading-[40px] heading-3">
              {title}
            </h2>
          </div>
          <div id="listCardContainer" className={styles.listContainer}>
            {cards.map((card, index) => (
              <div
                key={`listItem-item--${index}`}
                data-card-name="listWrapper"
                data-card-index={`listWrapper-${index}`}
                className={`${styles.listItem} ${
                  index === 0 ? 'listItemActive' : ''
                }`}
              >
                <div className={`${styles.listItemImage}`}>
                  <div
                    className={`flex justify-center mx-24 w-full  bg-display-200 pt-24`}
                  >
                    {fullImage ? (
                      <Image
                        src={card.ImageResponsive?.mobile.path}
                        width={card.ImageResponsive?.mobile.width ?? 241}
                        height={card.ImageResponsive?.mobile.height ?? 502}
                        alt={card.ImageResponsive.mobile.alternativeText}
                        quality={80}
                        style={{
                          objectPosition: 'center top',
                        }}
                        className={`w-full h-fit ${objectFit}`}
                      />
                    ) : (
                      <Image
                        src={card.ImageResponsive.mobile.path}
                        width={card.ImageResponsive?.mobile.width ?? 312}
                        height={card.ImageResponsive?.mobile.height ?? 436}
                        objectFit={
                          card.ImageResponsive.mobile.formats === 'contain' ||
                          objectFit === 'object-contain'
                            ? 'contain'
                            : 'cover'
                        }
                        objectPosition={`center top`}
                        alt={card.ImageResponsive.mobile.alternativeText}
                        quality={80}
                        className={`w-full h-fit ${objectFit} object-top`}
                      />
                    )}
                  </div>
                </div>
                <div
                  onClick={e => listCardItemAction(e, card)}
                  className={styles.listItemCard}
                >
                  <p className="mb-4 text-[16px] font-bold leading-[24px] paragraph-16">
                    {card.title}
                  </p>
                  <MarkdownContent
                    content={card.description}
                    className="font-normal text-[16px] leading-[24px] paragraph-16"
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="hidden lg:flex justify-start px-0 mx-auto mt-24 w-full">
            {children}
          </div>
        </div>
        {fullImage ? (
          <div
            id="listImageContainer"
            className="hidden md:flex justify-center p-40 lg:px-0 lg:my-auto lg:w-full min-w-[320px] max-w-[510px] h-full bg-display-200 rounded-[24px]"
          >
            <Image
              src={
                !!currentListCard?.ImageResponsive?.web.path
                  ? currentListCard.ImageResponsive?.web.path
                  : cards[0].ImageResponsive?.web.path
              }
              width={cards[0].ImageResponsive?.web.width ?? 241}
              height={cards[0].ImageResponsive?.web.height ?? 502}
              style={{ objectFit: 'fill' }}
              alt={cards[0].ImageResponsive.web.alternativeText}
              quality={80}
            />
          </div>
        ) : (
          <div
            id="listImageContainer"
            className="hidden md:flex justify-center p-16 w-full max-w-[510px] h-[582px] bg-display-200 rounded-[24px]"
          >
            <div className="relative w-full h-full">
              <Image
                src={
                  !!currentListCard?.ImageResponsive?.web.path
                    ? currentListCard.ImageResponsive?.web.path
                    : cards[0].ImageResponsive?.web.path
                }
                style={{ objectFit: 'contain' }}
                alt={cards[0].ImageResponsive.web.alternativeText}
                quality={80}
                layout="fill"
              />
            </div>
          </div>
        )}
      </div>
      <div className="flex lg:hidden justify-center px-24 mx-auto mt-24 w-full">
        {children}
      </div>
    </div>
  );
};

export default ListWithImages;
